import React, { useState } from "react";
import { Button, Card, Stack } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { banGuardian, banMember, patchGuardian, patchMember, postNote, postSummary } from "../../services/httpClient";
import SelectObject from "./SelectObject";
import Textarea from "./Textarea";

interface BanState {
  user: any;
  previousJob: string;
  isBanned: boolean;
  setIsBanning: any;
  userToBan: string | undefined;
  setUserToBan: any;
  fetchHousehold: any;
  resetCurrentView: any;
  type: string;
  caseStatus: string;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const ConfirmBan = ({
  user,
  previousJob,
  isBanned,
  setIsBanning,
  userToBan,
  setUserToBan,
  fetchHousehold,
  resetCurrentView,
  type,
  caseStatus,
  setShowToast,
  setToastText,
  setToastBody,
}: BanState) => {
  const { id } = useParams();
  const [needNote, setNeedNote] = useState<boolean>(false);
  const [banReason, setBanReason] = useState<string>("");
  const [duration, setDuration] = useState<string>("indefinite");
  const [allowBan, setAllowBan] = useState<boolean>(false);

  const durationOptions = [
    { key: "30", name: "30 Day" },
    { key: "60", name: "60 Day" },
    { key: "90", name: "90 Day" },
    { key: "180", name: "180 Day" },
    { key: "365", name: "1 Year" },
    { key: "indefinite", name: "Indefinite" },
  ];

  const onClickBanContinue = () => {
    setNeedNote(true);
  };

  const updateBanReason = (e: any) => {
    setBanReason(e.target.value);
    setAllowBan(true);
  };

  const updateBanDuration = (e: any) => {
    setDuration(e.target.value);
  };

  const onClickBanConfirm = () => {
    if (type === "Guardian") {
      if (caseStatus === "inactive") {
        const standardNote = {
          notetype: "standard",
          subject: {
            id: user?.id,
            type: "guardian",
          },
          fields: {
            text: banReason,
            date: new Date().toISOString().split("T")[0],
          },
        };

        postNote(standardNote, id)
          .then(() => {
            const banData = {
              is_banned: !isBanned,
              duration: duration,
              program: null,
              previous_job: previousJob,
              job: null,
            };

            banGuardian(banData, id, user?.id)
              .then(() => {
                resetCurrentView();
                fetchHousehold();
                setShowToast(true);
                setToastText(isBanned ? "Guardian Unbanned" : "Guardian Banned");
                setToastBody(userToBan);
                setIsBanning(false);
                setUserToBan("");
              })
              .catch((err: any) => {
                console.log(err);
              });

            postSummary(id)
              .then(() => {})
              .catch((err: any) => {
                console.log(err);
              });
          })
          .catch((err: any) => {
            console.log(err);
          });

        return;
      }

      if (isBanned) {
        //unban guardian
        //save intake note
        const intakeNote = {
          notetype: "intake",
          subject: {
            id: user?.id,
            type: "guardian",
          },
          fields: {
            description: banReason,
            date: new Date().toISOString().split("T")[0],
          },
        };

        postNote(intakeNote, id)
          .then((res: any) => {
            const noteId = res[0].data.note_id;

            //update user intake data
            const intakeData = {
              intake_at: new Date().toISOString().split("T")[0],
              intake_note_id: noteId,
              exit_at: null,
              exit_note_id: null,
            };

            patchGuardian(intakeData, id, user?.id).catch((err: any) => {
              console.log(err);
            });

            const unban = {
              is_banned: !isBanned,
              duration: duration,
              program: null,
              previous_job: previousJob,
              job: null,
            };

            banGuardian(unban, id, user?.id)
              .then((res: any) => {
                resetCurrentView();
                fetchHousehold();
                setShowToast(true);
                setToastText("Guardian Unbanned");
                setToastBody(userToBan);
                setIsBanning(false);
                setUserToBan("");
              })
              .catch((err: any) => {
                console.log(err);
              });

            //create new ai summary
            postSummary(id)
              .then((res: any) => {})
              .catch((err: any) => {
                console.log(err);
              });
          })
          .catch((err: any) => {
            console.log(err);
          });
      } else {
        //ban guardian
        const exitNote = {
          notetype: "exit",
          subject: {
            id: user?.id,
            type: "guardian",
          },
          fields: {
            exit_type: "ban",
            description: banReason,
            date: new Date().toISOString().split("T")[0],
          },
        };

        //save exit note
        postNote(exitNote, id)
          .then((res: any) => {
            const note_id = res[0].data.note_id;

            const exitData = {
              exit_at: new Date().toISOString().split("T")[0],
              exit_note_id: note_id,
            };

            patchGuardian(exitData, id, user?.id).catch((err: any) => {
              console.log(err);
            });

            const ban = {
              is_banned: !isBanned,
              duration: duration,
              program: null,
              previous_job: previousJob,
              job: null,
            };

            banGuardian(ban, id, user?.id)
              .then((res: any) => {
                resetCurrentView();
                fetchHousehold();
                setShowToast(true);
                setToastText("Guardian Banned");
                setToastBody(userToBan);
                setIsBanning(false);
                setUserToBan("");
              })
              .catch((err: any) => {
                console.log(err);
              });

            //create new ai summary
            postSummary(id)
              .then((res: any) => {})
              .catch((err: any) => {
                console.log(err);
              });
          })
          .catch((err: any) => {
            console.log(err);
          });
      }
    } else if (type === "Member") {
      if (caseStatus === "inactive") {
        const standardNote = {
          notetype: "standard",
          subject: {
            id: user?.id,
            type: "member",
          },
          fields: {
            text: banReason,
            date: new Date().toISOString().split("T")[0],
          },
        };

        postNote(standardNote, id)
          .then(() => {
            const banData = {
              is_banned: !isBanned,
              duration: duration,
              program: null,
              previous_job: previousJob,
              job: null,
            };

            banMember(banData, id, user?.id)
              .then(() => {
                resetCurrentView();
                fetchHousehold();
                setShowToast(true);
                setToastText(isBanned ? "Member Unbanned" : "Member Banned");
                setToastBody(userToBan);
                setIsBanning(false);
                setUserToBan("");
              })
              .catch((err: any) => {
                console.log(err);
              });

            postSummary(id)
              .then(() => {})
              .catch((err: any) => {
                console.log(err);
              });
          })
          .catch((err: any) => {
            console.log(err);
          });

        return;
      }

      if (isBanned) {
        //unban member
        //save intake note
        const intakeNote = {
          notetype: "intake",
          subject: {
            id: user?.id,
            type: "member",
          },
          fields: {
            description: banReason,
            date: new Date().toISOString().split("T")[0],
          },
        };

        postNote(intakeNote, id)
          .then((res: any) => {
            const noteId = res[0].data.note_id;

            //update user intake data
            const intakeData = {
              intake_at: new Date().toISOString().split("T")[0],
              intake_note_id: noteId,
              exit_at: null,
              exit_note_id: null,
            };

            patchMember(intakeData, id, user?.id).catch((err: any) => {
              console.log(err);
            });

            const unban = {
              is_banned: !isBanned,
              duration: duration,
              program: null,
              previous_job: previousJob,
              job: null,
            };

            banMember(unban, id, user?.id)
              .then((res: any) => {
                resetCurrentView();
                fetchHousehold();
                setShowToast(true);
                setToastText("Member Unbanned");
                setToastBody(userToBan);
                setIsBanning(false);
                setUserToBan("");
              })
              .catch((err: any) => {
                console.log(err);
              });

            //create new ai summary
            postSummary(id)
              .then((res: any) => {})
              .catch((err: any) => {
                console.log(err);
              });
          })
          .catch((err: any) => {
            console.log(err);
          });
      } else {
        //ban guardian
        const exitNote = {
          notetype: "exit",
          subject: {
            id: user?.id,
            type: "member",
          },
          fields: {
            exit_type: "ban",
            description: banReason,
            date: new Date().toISOString().split("T")[0],
          },
        };

        //save exit note
        postNote(exitNote, id)
          .then((res: any) => {
            const noteId = res[0].data.note_id;

            //update user exit data
            const exitData = {
              exit_at: new Date().toISOString().split("T")[0],
              exit_note_id: noteId,
            };

            patchMember(exitData, id, user?.id).catch((err: any) => {
              console.log(err);
            });

            const ban = {
              is_banned: !isBanned,
              duration: duration,
              program: null,
              previous_job: previousJob,
              job: null,
            };

            banMember(ban, id, user?.id)
              .then((res: any) => {
                resetCurrentView();
                fetchHousehold();
                setShowToast(true);
                setToastText("Member Banned");
                setToastBody(userToBan);
                setIsBanning(false);
                setUserToBan("");
              })
              .catch((err: any) => {
                console.log(err);
              });

            //create new ai summary
            postSummary(id)
              .then((res: any) => {})
              .catch((err: any) => {
                console.log(err);
              });
          })
          .catch((err: any) => {
            console.log(err);
          });
      }
    }
  };

  const onClickCancelBan = () => {
    setIsBanning(false);
    setNeedNote(false);
    setUserToBan("");
    setAllowBan(false);
  };

  return needNote ? (
    <Card className="bg-light mb-3">
      <Card.Body>
        <Stack direction="horizontal" gap={3}>
          <Stack style={{ justifyContent: "center" }}>
            <Textarea
              label={isBanned ? "Enter Unban Reason" : "Enter Ban Reason"}
              value={banReason}
              hasFocus={true}
              rows={1}
              onUpdate={(e: any) => updateBanReason(e)}
              css={""}
            />
          </Stack>
          {!isBanned && (
            <SelectObject label="Duration" value={duration} options={durationOptions} onUpdate={(e: any) => updateBanDuration(e)} />
          )}
          <Button className="app-danger-color button" onClick={() => onClickBanConfirm()} disabled={!allowBan}>
            Confirm {isBanned ? "Unban" : "Ban"}
          </Button>
          <div className="vr" />
          <Button className="app-primary-color button" onClick={() => onClickCancelBan()}>
            Cancel
          </Button>
        </Stack>
      </Card.Body>
    </Card>
  ) : (
    <Card className="bg-light mb-3">
      <Card.Body>
        <Stack direction="horizontal" gap={3}>
          <Stack style={{ justifyContent: "center" }}>
            <Card.Subtitle>
              Are you sure you want to {isBanned ? "unban" : "ban"} {userToBan}?
            </Card.Subtitle>
          </Stack>
          <Button className="app-danger-color button" onClick={() => onClickBanContinue()}>
            Continue
          </Button>
          <div className="vr" />
          <Button className="app-primary-color button" onClick={() => onClickCancelBan()}>
            Cancel
          </Button>
        </Stack>
      </Card.Body>
    </Card>
  );
};

export default ConfirmBan;
